import { forwardRef } from 'react';
import type { AnchorHTMLAttributes, ButtonHTMLAttributes, ReactNode } from 'react';
import { Loader2 } from 'lucide-react';
import { cn } from '../../lib/utils';

export type ButtonVariant =
  | 'primary'
  | 'secondary'
  | 'ghost'
  | 'outline'
  | 'danger'
  | 'link'
  | 'soft';

export type ButtonSize = 'xs' | 'sm' | 'md' | 'lg' | 'icon' | 'icon-sm' | 'icon-lg';

const VARIANTS: Record<ButtonVariant, string> = {
  primary:
    'bg-accent text-accent-fg shadow-hair hover:bg-accent-hover active:bg-accent-hover active:brightness-95',
  secondary:
    'bg-elevated text-fg border border-border hover:border-border-strong hover:bg-surface active:bg-elevated',
  ghost: 'text-muted hover:text-fg hover:bg-elevated active:bg-sunken',
  outline:
    'border border-border text-fg hover:border-border-strong hover:bg-elevated active:bg-sunken',
  danger: 'bg-danger text-white shadow-hair hover:brightness-110 active:brightness-95',
  soft: 'bg-accent-soft text-accent hover:brightness-105 active:brightness-95',
  link: 'text-accent underline-offset-4 hover:underline',
};

const SIZES: Record<ButtonSize, string> = {
  xs: 'h-7 px-2.5 text-xs gap-1.5 rounded-md',
  sm: 'h-8 px-3 text-[13px] gap-1.5 rounded-md',
  md: 'h-9 px-4 text-sm gap-2 rounded-md',
  lg: 'h-11 px-5 text-[15px] gap-2 rounded-lg',
  icon: 'size-9 rounded-md',
  'icon-sm': 'size-8 rounded-md',
  'icon-lg': 'size-11 rounded-lg',
};

/** `link` opts out of the box model so it can sit inline in a sentence. */
const LINK_SIZES: Partial<Record<ButtonSize, string>> = {
  xs: 'text-xs',
  sm: 'text-[13px]',
  md: 'text-sm',
  lg: 'text-[15px]',
};

const BASE = cn(
  'relative inline-flex select-none items-center justify-center font-medium whitespace-nowrap',
  'transition-[background-color,border-color,color,box-shadow,transform] duration-150',
  'focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring',
  'disabled:pointer-events-none disabled:opacity-50',
);

function buttonClasses(
  variant: ButtonVariant,
  size: ButtonSize,
  fullWidth: boolean | undefined,
  className: string | undefined,
): string {
  return cn(
    BASE,
    VARIANTS[variant],
    variant === 'link' ? cn('h-auto gap-1 p-0', LINK_SIZES[size]) : SIZES[size],
    fullWidth && 'w-full',
    className,
  );
}

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant;
  size?: ButtonSize;
  /** Swaps the leading icon for a spinner and blocks interaction. */
  loading?: boolean;
  /** Announced while `loading` — falls back to the button label. */
  loadingLabel?: string;
  leftIcon?: ReactNode;
  rightIcon?: ReactNode;
  fullWidth?: boolean;
}

/**
 * The one button. Every action in the app renders through it so focus rings,
 * disabled states and loading behaviour stay identical everywhere.
 *
 * While `loading` the label keeps its place (the spinner replaces the leading
 * icon) so the button never changes width mid-request.
 */
export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  {
    className,
    variant = 'primary',
    size = 'md',
    loading = false,
    loadingLabel,
    leftIcon,
    rightIcon,
    fullWidth,
    children,
    disabled,
    type = 'button',
    ...props
  },
  ref,
) {
  const iconOnly = size === 'icon' || size === 'icon-sm' || size === 'icon-lg';
  const spinner = <Loader2 className="size-4 shrink-0 animate-spin" aria-hidden />;

  return (
    <button
      ref={ref}
      type={type}
      disabled={disabled || loading}
      aria-busy={loading || undefined}
      className={buttonClasses(variant, size, fullWidth, className)}
      {...props}
    >
      {loading ? spinner : leftIcon}
      {iconOnly && loading ? null : children}
      {!loading && rightIcon}
      {loading && loadingLabel && <span className="sr-only">{loadingLabel}</span>}
    </button>
  );
});

export interface ButtonLinkProps extends AnchorHTMLAttributes<HTMLAnchorElement> {
  variant?: ButtonVariant;
  size?: ButtonSize;
  leftIcon?: ReactNode;
  rightIcon?: ReactNode;
  fullWidth?: boolean;
}

/** Same visual language as `Button`, for real anchors (external links, OAuth). */
export const ButtonLink = forwardRef<HTMLAnchorElement, ButtonLinkProps>(function ButtonLink(
  { className, variant = 'secondary', size = 'md', leftIcon, rightIcon, fullWidth, children, ...props },
  ref,
) {
  return (
    <a ref={ref} className={buttonClasses(variant, size, fullWidth, className)} {...props}>
      {leftIcon}
      {children}
      {rightIcon}
    </a>
  );
});
